import { initWasm, clearKeys, hasKeys } from './crypto.js';
import { loadConfig } from './config.js';
import { register, start, navigate } from './router.js';
import { getToken, getEmail, getRememberedEmail, clearSession, isLimited } from './auth-store.js';
import { renderLogin } from './pages/login.js';
import { renderRegister } from './pages/register.js';
import { renderVerifyEmail } from './pages/verify-email.js';
import { renderItemsList } from './pages/items-list.js';
import { renderItemCreate } from './pages/item-create.js';
import { renderItemDetail } from './pages/item-detail.js';
import { renderGrantsList } from './pages/grants-list.js';
import { renderGrantCreate } from './pages/grant-create.js';
import { renderGrantAccess } from './pages/grant-access.js';
import { renderGrantAccept } from './pages/grant-accept.js';
import { renderAuditLog } from './pages/audit-log.js';
import { renderMagicLink } from './pages/magic-link.js';
import { renderSetupPassword } from './pages/setup-password.js';
import { renderUnlock } from './pages/unlock.js';
import { renderBilling } from './pages/billing.js';
import { renderLegacy } from './pages/legacy.js';
import { renderSettings } from './pages/settings.js';
import { renderSetupCredential } from './pages/setup-credential.js';
import { renderSetupPasskey } from './pages/setup-passkey.js';
import { renderDropLanding } from './pages/drop-landing.js';
import { renderDropDownload } from './pages/drop-download.js';
import { renderDropClaim } from './pages/drop-claim.js';
import { renderGrantClaim } from './pages/grant-claim.js';
import { renderWillAccess } from './pages/will-access.js';
import { renderRecover } from './pages/recover.js';
import { renderPickup } from './pages/pickup.js';
import { renderFaq } from './pages/faq.js';
import { renderArchitecture } from './pages/architecture.js';
import { renderPrivacy } from './pages/privacy.js';
import { renderCompare } from './pages/compare.js';
import { renderNotarizations } from './pages/notarizations.js';
import { renderNotarizationDetail } from './pages/notarization-detail.js';
import { renderVerify } from './pages/verify.js';
import { renderDeadDropSend } from './pages/dead-drop-send.js';
import { renderDeadDropManage } from './pages/dead-drop-manage.js';
import { renderCloseAccount } from './pages/close-account.js';
import { renderAccount } from './pages/account.js';
import { renderCancelCloseAccount } from './pages/cancel-close-account.js';
import { renderUsbVault } from './pages/usb-vault.js';
import { isTauri } from './backend.js';

// DOM elements
const landing = document.getElementById('landing');
const loginScreen = document.getElementById('login-screen');
const fullscreenPage = document.getElementById('fullscreen-page');
const appShell = document.getElementById('app');
const viewContainer = document.getElementById('viewContainer');
const mobileToggle = document.getElementById('mobileToggle');
const sidebarOverlay = document.getElementById('sidebarOverlay');
const sidebar = document.getElementById('sidebar');

// ── Screen management ──
export function showScreen(name) {
  landing.style.display = 'none';
  landing.classList.remove('skeleton-active');
  loginScreen.style.display = 'none';
  fullscreenPage.style.display = 'none';
  appShell.classList.remove('visible');
  mobileToggle.style.display = 'none';

  switch (name) {
    case 'landing':
      landing.style.display = 'flex';
      break;
    case 'login':
      loginScreen.style.display = 'flex';
      break;
    case 'fullscreen':
      fullscreenPage.style.display = 'flex';
      break;
    case 'app':
      appShell.classList.add('visible');
      mobileToggle.style.display = '';
      renderSidebar();
      break;
  }
}

// ── Topbar helper ──
export function setTopbar(title, actionsHtml = '') {
  document.getElementById('topbar-title').textContent = title;
  document.getElementById('topbar-actions').innerHTML = actionsHtml;
  if (actionsHtml) lucideIcons();
}

// ── Sidebar ──
// `limitedOk: true` marks nav items that work in a Limited (magic-link-only)
// session — i.e. don't need the vault master key on the client. Everything
// else is hidden from the sidebar when isLimited(), to avoid dead-end clicks
// that would just redirect to /account.
const NAV_ITEMS = [
  { view: 'account', icon: 'user', label: 'Account', hash: '#/account', limitedOk: true, limitedOnly: true },
  { view: 'items', icon: 'box', label: 'Items', hash: '#/items' },
  { view: 'grants', icon: 'share-2', label: 'Grants', hash: '#/grants' },
  { view: 'drop', icon: 'upload-cloud', label: 'Drop', hash: '#/drop' },
  { view: 'billing', icon: 'credit-card', label: 'Billing', hash: '#/billing', limitedOk: true },
  { view: 'legacy', icon: 'scroll-text', label: 'Legacy', hash: '#/legacy' },
  { view: 'dead-drops', icon: 'inbox', label: 'Inboxes', hash: '#/dead-drops' },
  { view: 'notarizations', icon: 'stamp', label: 'Notarize', hash: '#/notarizations' },
  { view: 'settings', icon: 'settings', label: 'Settings', hash: '#/settings', limitedOk: true },
  { view: 'audit-log', icon: 'file-text', label: 'Audit Log', hash: '#/audit-log' },
].concat(isTauri ? [{ view: 'usb-vault', icon: 'hard-drive', label: 'USB Vault', hash: '#/usb-vault' }] : []);

function renderSidebar() {
  const nav = document.getElementById('sidebar-nav');
  const limited = isLimited();
  const items = NAV_ITEMS.filter(n => limited ? n.limitedOk : !n.limitedOnly);
  nav.innerHTML = items.map(n =>
    `<a class="nav-item" data-view="${n.view}" href="${n.hash}"><i data-lucide="${n.icon}"></i><span>${n.label}</span></a>`
  ).join('');

  if (limited) {
    nav.insertAdjacentHTML(
      'afterbegin',
      `<a class="nav-item" data-view="unlock" href="#/unlock" style="background:var(--accent);color:#0c0e14"><i data-lucide="lock-open"></i><span>Unlock vault</span></a>`
    );
  }

  renderSidebarFooter();
  updateActiveNav();
  lucideIcons();
}

async function updateUsbStatusBar() {
  const bar = document.getElementById('usb-status-bar');
  if (!bar) return;
  try {
    const status = await window.__TAURI_INTERNALS__.invoke('vault_status');
    if (status.usb_connected && status.unlocked) {
      bar.innerHTML = `<span class="status-dot online"></span> <span>${status.item_count} items</span>`;
      bar.className = 'usb-status-bar connected';
    } else if (status.usb_connected) {
      bar.innerHTML = `<span class="status-dot offline"></span> <span>USB locked</span>`;
      bar.className = 'usb-status-bar';
    } else {
      bar.innerHTML = `<span class="status-dot offline"></span> <span>No USB</span>`;
      bar.className = 'usb-status-bar';
    }
  } catch (_) {
    bar.innerHTML = '';
  }
}

function renderSidebarFooter() {
  const footer = document.getElementById('sidebar-footer');
  const email = getEmail() || '';
  const initial = email ? email[0].toUpperCase() : '?';
  const name = email.split('@')[0] || 'User';

  footer.innerHTML = `
    ${isTauri ? '<div class="usb-status-bar" id="usb-status-bar"></div>' : ''}
    <div class="avatar">${initial}</div>
    <div class="user-info">
      <div class="user-name">${esc(name)}</div>
      <div class="user-email">${esc(email)}</div>
    </div>
    <div class="logout-btn" id="logout-btn"><i data-lucide="log-out" style="width:18px;height:18px"></i></div>
  `;

  document.getElementById('logout-btn').onclick = () => {
    clearSession();
    clearKeys();
    navigate('#/drop');
  };

  // Update USB status indicator in Tauri mode
  if (isTauri) {
    updateUsbStatusBar();
  }

  lucideIcons();
}

export function updateActiveNav() {
  const hash = window.location.hash;
  document.querySelectorAll('.nav-item').forEach(item => {
    const view = item.dataset.view;
    const isActive = hash.startsWith(item.getAttribute('href'));
    item.classList.toggle('active', isActive);
  });
}

// ── Mobile sidebar ──
function openSidebar() {
  sidebar.classList.add('open');
  sidebarOverlay.classList.add('visible');
}

function closeSidebar() {
  sidebar.classList.remove('open');
  sidebarOverlay.classList.remove('visible');
}

mobileToggle.onclick = openSidebar;
sidebarOverlay.onclick = closeSidebar;

// ── Auth helpers ──
function isLoggedIn() {
  return !!getToken();
}

// Full vault access — token AND derived keys in memory. Anything that
// needs to decrypt vault content uses this guard. Limited sessions (magic
// link only) get bounced to the /account limited surface, where they can
// either manage their account or click "Unlock vault" to upgrade.
function requireAuth(renderFn) {
  return (params) => {
    if (!isLoggedIn()) { navigate('#/drop'); return; }
    if (isLimited()) { navigate('#/account'); return; }
    if (!hasKeys()) { navigate('#/unlock'); return; }
    showScreen('app');
    updateActiveNav();
    renderFn(viewContainer, params);
    lucideIcons();
  };
}

// Token only — no vault-key requirement. Used by /settings, /close-account,
// and /account. Works for both Limited and Full sessions; pages that care
// about the distinction check `isLimited()` / `hasKeys()` themselves.
function requireLogin(renderFn) {
  return (params) => {
    if (!isLoggedIn()) { navigate('#/drop'); return; }
    showScreen('app');
    updateActiveNav();
    renderFn(viewContainer, params);
    lucideIcons();
  };
}

// Render into fullscreen-page container
function fullscreenRoute(renderFn) {
  return (params) => {
    showScreen('fullscreen');
    renderFn(fullscreenPage, params);
    lucideIcons();
  };
}

// ── Lucide icons helper ──
export function lucideIcons() {
  try { lucide.createIcons(); } catch (_) {}
}

// ── Init ──
async function init() {
  await Promise.all([
    initWasm(),
    loadConfig().catch(e => console.warn('Config load failed (offline?):', e)),
  ]);

  // Pre-register service worker for streaming downloads (fire-and-forget)
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.register('/sw.js').catch(() => {});
  }

  // Landing (drop zone) — full-page when logged out, in-app when logged in
  register('/drop', (p) => {
    if (isLoggedIn() && hasKeys()) {
      showScreen('app');
      updateActiveNav();
      setTopbar('Drop', '');
      renderDropLanding(viewContainer, p);
    } else {
      showScreen('landing');
      renderDropLanding(landing, p);
    }
    lucideIcons();
  });

  // Login
  register('/login', (p) => {
    showScreen('login');
    renderLogin(loginScreen, p);
    lucideIcons();
  });

  // Fullscreen unauthenticated pages
  register('/register', fullscreenRoute(renderRegister));
  register('/verify-email', fullscreenRoute(renderVerifyEmail));
  register('/magic-link', fullscreenRoute(renderMagicLink));
  register('/cancel-close-account', fullscreenRoute(renderCancelCloseAccount));
  register('/setup-password', fullscreenRoute(renderSetupPassword));
  register('/setup-credential', fullscreenRoute(renderSetupCredential));
  register('/setup-passkey', fullscreenRoute(renderSetupPasskey));
  register('/unlock', fullscreenRoute(renderUnlock));
  register('/recover', fullscreenRoute(renderRecover));
  register('/faq', (p) => {
    showScreen('landing');
    renderFaq(landing, p);
    lucideIcons();
  });
  register('/architecture', (p) => {
    showScreen('landing');
    renderArchitecture(landing, p);
    lucideIcons();
  });
  register('/privacy', (p) => {
    showScreen('landing');
    renderPrivacy(landing, p);
    lucideIcons();
  });
  register('/compare', (p) => {
    showScreen('landing');
    renderCompare(landing, p);
    lucideIcons();
  });

  // Unauthenticated grant/drop pages
  register('/grant-accept/:id/:secret', fullscreenRoute(renderGrantAccept));
  register('/grant-accept/:id', fullscreenRoute(renderGrantAccept));
  register('/will/:token', fullscreenRoute(renderWillAccess));
  register('/pickup', (p) => {
    if (isLoggedIn() && hasKeys()) {
      showScreen('app');
      updateActiveNav();
      setTopbar('Pick Up', '');
      renderPickup(viewContainer, p);
    } else {
      showScreen('fullscreen');
      renderPickup(fullscreenPage, p);
    }
    lucideIcons();
  });
  register('/pickup/:id', (p) => {
    if (isLoggedIn() && hasKeys()) {
      showScreen('app');
      updateActiveNav();
      setTopbar('Pick Up', '');
      renderPickup(viewContainer, p);
    } else {
      showScreen('fullscreen');
      renderPickup(fullscreenPage, p);
    }
    lucideIcons();
  });
  register('/drop/:id', fullscreenRoute(renderDropDownload));
  register('/drop/:id/claim', fullscreenRoute(renderDropClaim));
  register('/grant-claim/:id', fullscreenRoute(renderGrantClaim));
  register('/inbox/:id_or_slug', fullscreenRoute(renderDeadDropSend));

  // Authenticated pages
  register('/items', requireAuth(renderItemsList));
  register('/items/new', requireAuth(renderItemCreate));
  register('/items/:id', requireAuth(renderItemDetail));
  register('/groups', () => navigate('#/items?view=collections'));
  register('/groups/new', () => navigate('#/items?view=collections'));
  register('/groups/:id', (p) => navigate(`#/items?view=collections&group=${p.id}`));
  register('/grants', requireAuth(renderGrantsList));
  register('/grants/new', requireAuth(renderGrantCreate));
  register('/grants/:id/access', requireAuth(renderGrantAccess));
  // Billing is Limited-compatible — the page only hits /billing endpoints,
  // which don't touch vault content, so it's reachable from a magic-link session.
  register('/billing', requireLogin(renderBilling));
  register('/legacy', requireAuth(renderLegacy));
  register('/account', requireLogin(renderAccount));
  register('/settings', requireLogin(renderSettings));
  register('/audit-log', requireAuth(renderAuditLog));
  register('/dead-drops', requireAuth(renderDeadDropManage));
  register('/close-account', requireLogin(renderCloseAccount));
  register('/usb-vault', requireAuth(renderUsbVault));
  register('/notarizations', requireAuth(renderNotarizations));
  register('/notarizations/:id', requireAuth(renderNotarizationDetail));
  register('/verify', (p) => {
    showScreen('landing');
    renderVerify(landing, p);
    lucideIcons();
  });

  // /will without token: show passphrase entry for heirs, or redirect to legacy for logged-in users
  register('/will', (p) => {
    if (getToken()) { navigate('#/legacy'); return; }
    showScreen('fullscreen');
    renderWillAccess(fullscreenPage, {});
  });
  register('/deadman', (p) => { navigate('#/legacy'); });
  register('/passkeys', (p) => { navigate('#/settings'); });

  window.addEventListener('hashchange', () => {
    closeSidebar();
    updateActiveNav();
  });

  // Clear cryptographic keys when the tab/browser is closed
  window.addEventListener('beforeunload', () => clearKeys());

  // If we have a remembered email but no active session, redirect to
  // login with passkey hint so FaceID/TouchID triggers automatically.
  const rememberedEmail = getRememberedEmail();
  if (rememberedEmail && !getToken()) {
    const hash = window.location.hash.slice(1).split('?')[0];
    // Only auto-redirect for protected routes or empty hash — don't
    // hijack public pages like /drop/:id, /grant-accept, /inbox, etc.
    const isProtectedOrEmpty = !hash || hash === '/' || hash === '/items'
      || hash === '/grants' || hash === '/settings' || hash === '/billing'
      || hash === '/legacy' || hash === '/audit-log' || hash === '/dead-drops'
      || hash === '/notarizations' || hash === '/close-account';
    if (isProtectedOrEmpty) {
      window.location.hash = `#/login?email=${encodeURIComponent(rememberedEmail)}&autoPasskey=1`;
    }
  }

  start();
}

export function esc(s) {
  if (s == null) return '';
  const d = document.createElement('div');
  d.textContent = s;
  return d.innerHTML;
}

init().catch(err => {
  document.body.innerHTML = `<div style="padding:2rem"><div class="msg msg-error">Failed to initialize: ${esc(err.message)}</div></div>`;
});
